import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const SplashScreen = ({ onComplete }: { onComplete: () => void }) => {
  const [phase, setPhase] = useState<"logo" | "text" | "exit">("logo");

  useEffect(() => {
    const t1 = setTimeout(() => setPhase("text"), 400);
    const t2 = setTimeout(() => setPhase("exit"), 1200);
    const t3 = setTimeout(onComplete, 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [onComplete]);

  return (
    <AnimatePresence>
      {phase !== "exit" ? null : null}
      <motion.div
        key="splash"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        animate={phase === "exit" ? { opacity: 0, scale: 1.05 } : { opacity: 1 }}
        transition={{ duration: 0.5, ease: "easeInOut" }}
        className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-background"
      >
        {/* Ambient glow */}
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.4 }}
            transition={{ duration: 1.5 }}
            className="absolute left-1/2 top-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full"
            style={{
              background: "radial-gradient(circle, hsla(145,80%,55%,0.15) 0%, hsla(290,60%,65%,0.08) 40%, transparent 70%)",
            }}
          />
        </div>

        {/* Logo */}
        <motion.img
          src="/krew-logo.png"
          alt="Krew"
          className="relative z-10 h-20 w-20 drop-shadow-2xl"
          fetchPriority="high"
          initial={{ scale: 0.3, opacity: 0, rotate: -30 }}
          animate={{ scale: 1, opacity: 1, rotate: 0 }}
          transition={{ duration: 0.6, ease: [0.34, 1.56, 0.64, 1] }}
        />

        {/* Text */}
        <motion.div
          className="relative z-10 mt-5 overflow-hidden"
          initial={{ opacity: 0, y: 10 }}
          animate={phase === "text" || phase === "exit" ? { opacity: 1, y: 0 } : { opacity: 0, y: 10 }}
          transition={{ duration: 0.4 }}
        >
          <span className="text-3xl font-bold tracking-tight text-gradient-logo">
            Krew
          </span>
        </motion.div>

        <motion.p
          className="relative z-10 mt-2 text-xs font-medium tracking-widest uppercase text-muted-foreground"
          initial={{ opacity: 0 }}
          animate={phase === "text" || phase === "exit" ? { opacity: 1 } : { opacity: 0 }}
          transition={{ duration: 0.4, delay: 0.15 }}
        >
          The Social Nightlife OS
        </motion.p>

        {/* Loading bar */}
        <motion.div
          className="relative z-10 mt-8 h-0.5 w-32 overflow-hidden rounded-full bg-muted"
        >
          <motion.div
            className="h-full rounded-full"
            style={{ background: "linear-gradient(90deg, hsl(145,80%,55%), hsl(290,60%,65%), hsl(220,75%,65%))" }}
            initial={{ width: "0%" }}
            animate={{ width: "100%" }}
            transition={{ duration: 1.8, ease: "easeInOut" }}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SplashScreen;
